import React, { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { moveTask } from '../lib/api';
import { formatTime } from '../lib/utils';
import { useTimer } from '../lib/TimerContext';
import { Clock, Calendar, User, Play, GripVertical, AlertCircle, Timer, Target } from 'lucide-react';
import { toast } from 'sonner';

const COLUMNS = [
  { id: 'todo', title: 'K řešení', color: 'border-gray-400', bgColor: 'bg-gray-50' },
  { id: 'in_progress', title: 'Rozpracováno', color: 'border-[#006CB6]', bgColor: 'bg-blue-50' },
  { id: 'done', title: 'Hotovo', color: 'border-green-500', bgColor: 'bg-green-50' },
];

// Draggable Task Card
const TaskCard = ({ task, onStartTimer, isTimerRunning, onTaskClick, isDragOverlay = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: task.id,
    data: { type: 'task', task },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done';
  const isPriorityUrgent = task.priority === 'urgent';

  const handleCardClick = (e) => {
    // Don't trigger click if clicking on drag handle or timer button
    if (e.target.closest('[data-drag-handle]') || e.target.closest('[data-timer-btn]')) {
      return;
    }
    if (onTaskClick) {
      onTaskClick(task);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleCardClick}
      className={`bg-white border-2 ${isDragging ? 'border-[#006CB6] shadow-lg z-50' : 'border-gray-200'} p-3 mb-2 hover:border-black transition-colors ${onTaskClick ? 'cursor-pointer' : ''} ${isDragOverlay ? 'shadow-xl' : ''}`}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start gap-2">
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          data-drag-handle
          className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing mt-1 touch-none"
        >
          <GripVertical size={16} />
        </div>

        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-serif font-medium text-sm leading-tight">
              {task.title}
            </h4>
            {isPriorityUrgent && (
              <span className="shrink-0 px-1.5 py-0.5 text-xs font-oswald uppercase bg-red-100 text-red-700 border border-red-300">
                Urgentní
              </span>
            )}
          </div>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
            {task.estimated_minutes > 0 && (
              <span className="flex items-center gap-1 text-blue-600">
                <Target size={12} />
                {formatTime(task.estimated_minutes)}
              </span>
            )}
            {task.total_time_minutes > 0 && (
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {formatTime(task.total_time_minutes)}
              </span>
            )}
            {task.deadline && (
              <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                <Calendar size={12} />
                {task.deadline}
                {isOverdue && <AlertCircle size={12} />}
              </span>
            )}
          </div>

          {/* Assigned users */}
          {task.assigned_names?.length > 0 && (
            <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
              <User size={12} />
              <span className="truncate">{task.assigned_names.join(', ')}</span>
            </div>
          )}

          {/* Quick actions */}
          <div className="flex items-center gap-2 mt-2">
            <button
              data-timer-btn
              onClick={(e) => {
                e.stopPropagation();
                onStartTimer(task);
              }}
              disabled={isTimerRunning}
              className={`flex items-center gap-1 text-xs font-oswald uppercase px-2 py-1 border transition-colors ${
                isTimerRunning
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-[#006CB6] text-[#006CB6] hover:bg-[#006CB6] hover:text-white'
              }`}
              title={isTimerRunning ? 'Nejprve zastavte běžící timer' : 'Spustit timer'}
            >
              {isTimerRunning ? <Timer size={12} /> : <Play size={12} />}
              {isTimerRunning ? 'Timer běží' : 'Start'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Droppable Column component
const Column = ({ column, tasks, onStartTimer, isTimerRunning, onTaskClick }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { type: 'column', status: column.id },
  });

  const taskIds = useMemo(() => tasks.map(t => t.id), [tasks]);

  return (
    <div className="flex-1 min-w-[280px] max-w-[350px]">
      <div 
        ref={setNodeRef}
        className={`border-t-4 ${column.color} ${column.bgColor} p-3 min-h-[400px] transition-colors ${isOver ? 'ring-2 ring-[#006CB6] ring-inset' : ''}`}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-oswald text-sm font-bold uppercase tracking-wide">
            {column.title}
          </h3>
          <span className="bg-white px-2 py-0.5 text-xs font-oswald border border-gray-200">
            {tasks.length}
          </span>
        </div>

        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          <div className="min-h-[100px]">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onStartTimer={onStartTimer}
                isTimerRunning={isTimerRunning}
                onTaskClick={onTaskClick}
              />
            ))}
            {tasks.length === 0 && (
              <div className={`border-2 border-dashed ${isOver ? 'border-[#006CB6] bg-blue-100' : 'border-gray-300'} p-4 text-center text-gray-400 text-sm font-serif transition-colors`}>
                {isOver ? 'Pusťte sem' : 'Prázdný sloupec'}
              </div>
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};

// Main Board component
const TaskBoard = ({ tasks, projectId, onTasksChange, onTaskClick }) => {
  const [activeId, setActiveId] = useState(null);
  const { isRunning, startTimer } = useTimer();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter and group tasks by status
  const filteredTasks = useMemo(() => {
    return tasks.filter(t => !projectId || t.project_id === projectId);
  }, [tasks, projectId]);

  const tasksByStatus = useMemo(() => {
    const grouped = {
      todo: [],
      in_progress: [],
      done: [],
    };

    filteredTasks.forEach(task => {
      const status = task.status || 'todo';
      if (grouped[status]) {
        grouped[status].push(task);
      } else {
        grouped.todo.push(task);
      }
    });

    // Sort each column by order
    Object.keys(grouped).forEach(status => {
      grouped[status].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return grouped;
  }, [filteredTasks]);

  const activeTask = activeId ? filteredTasks.find(t => t.id === activeId) : null;

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    // Optional: handle drag over for visual feedback
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeTask = filteredTasks.find(t => t.id === active.id);
    if (!activeTask) return;

    let targetStatus = activeTask.status;
    let targetOrder = activeTask.order || 0;

    // Determine what we dropped over
    const overId = over.id;
    const overData = over.data?.current;

    if (overData?.type === 'column') {
      // Dropped directly on a column (empty area)
      targetStatus = overData.status;
      const columnTasks = tasksByStatus[targetStatus];
      targetOrder = columnTasks.length > 0 
        ? Math.max(...columnTasks.map(t => t.order || 0)) + 1 
        : 0;
    } else {
      // Dropped on a task - find that task's status and calculate order
      const overTask = filteredTasks.find(t => t.id === overId);
      if (overTask) {
        targetStatus = overTask.status;
        
        // Calculate new order based on position
        const columnTasks = tasksByStatus[targetStatus].filter(t => t.id !== activeTask.id);
        const overIndex = columnTasks.findIndex(t => t.id === overId);
        
        if (overIndex === -1) {
          // Task not found, put at end
          targetOrder = columnTasks.length > 0 
            ? Math.max(...columnTasks.map(t => t.order || 0)) + 1 
            : 0;
        } else {
          // Insert at the position of the over task
          targetOrder = overTask.order || overIndex;
        }
      }
    }

    // Skip if nothing changed
    if (activeTask.status === targetStatus && activeTask.order === targetOrder) {
      return;
    }

    try {
      await moveTask(activeTask.id, {
        new_status: targetStatus,
        new_order: targetOrder,
      });
      
      // Refresh tasks
      if (onTasksChange) {
        onTasksChange();
      }
      
      toast.success(`Úkol přesunut do "${COLUMNS.find(c => c.id === targetStatus)?.title}"`);
    } catch (error) {
      toast.error('Nepodařilo se přesunout úkol');
      console.error('Move task error:', error);
    }
  };

  const handleStartTimer = async (task) => {
    try {
      await startTimer({
        project_id: task.project_id,
        project_name: task.project_name,
        task_id: task.id,
        task_name: task.title,
        description: '',
      });
    } catch (error) {
      // Error handled in TimerContext
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="task-board">
        {COLUMNS.map((column) => (
          <Column
            key={column.id}
            column={column}
            tasks={tasksByStatus[column.id]}
            onStartTimer={handleStartTimer}
            isTimerRunning={isRunning}
            onTaskClick={onTaskClick}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeTask ? (
          <div className="w-[300px]">
            <TaskCard
              task={activeTask}
              onStartTimer={() => {}}
              isTimerRunning={false}
              isDragOverlay
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default TaskBoard;
